import type { Tone } from '../../components/tone';
import { pct } from '../../lib/numbers';
import { rule } from '../../lib/rules';
import { getSettings } from '../../lib/settingsStore';
import type { Shipment } from '@serb/shared';
import { useShipments } from '../shipments/useShipments';
import type { Carrier, CarrierRate } from './sample';
import { useCarrierRates } from './useCarriers';

/** Rate competitiveness: on each route and service where another carrier also gave a price (same currency and unit), the lowest price divided by this carrier's price (100% = cheapest). The average. `null` with nothing to compare. */
function rateCompetitiveness(rates: CarrierRate[], all: CarrierRate[]): number | null {
  const scores = rates.flatMap((r) => {
    const others = all.filter(
      (o) =>
        o.carrierId !== r.carrierId &&
        o.route === r.route &&
        o.service === r.service &&
        o.currency === r.currency &&
        o.per === r.per,
    );
    return others.length ? [Math.min(r.rate, ...others.map((o) => o.rate)) / r.rate] : [];
  });
  return scores.length ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) : null;
}

export type RolloverRisk = 'Low' | 'Medium' | 'High' | 'Not enough data';

export const RISK_TONE: Record<RolloverRisk, Tone> = {
  Low: 'success',
  Medium: 'warning',
  High: 'error',
  'Not enough data': 'neutral',
};

/** Rollover risk from the share of bookings rolled over: up to the acceptable rate is Low, from the "high" rate on is High, in between Medium. Needs at least 2 bookings. The rates are set in Settings. */
export function rolloverRisk(rolled: number, total: number): RolloverRisk {
  if (total < rule('minBookingsForRollover')) return 'Not enough data';
  const share = pct(rolled, total);
  const { rolloverAcceptable, rolloverHigh } = getSettings().kpis;
  return share <= rolloverAcceptable ? 'Low' : share < rolloverHigh ? 'Medium' : 'High';
}

export type RouteRow = { route: string; shipments: number; rolledOver: number; delayed: number; rates: number };

const CLOSED = ['Lost', 'Cancelled', 'Merged'];

/** What we know about a carrier, counted from our shipments and the rates it gave (`allRates`). Shipments name it by its name or an alias. */
export function statsFrom(c: Carrier, allRates: CarrierRate[], allShips: Shipment[]) {
  const rates = allRates.filter((r) => r.carrierId === c.id);
  const names = c.aliases.map((a) => a.toLowerCase());
  const ships = allShips.filter((s) => names.includes(s.carrier.toLowerCase()));
  const total = ships.length;
  const arrived = ships.filter((s) => s.stage === 'Arrived' || s.stage === 'Completed').length;
  const delayed = ships.filter((s) => s.stage === 'Delayed').length;
  const rolledOver = ships.filter((s) => s.rollovers > 0).length;
  // on schedule = neither delayed nor rolled over
  const onSchedule = ships.filter((s) => s.stage !== 'Delayed' && s.rollovers === 0).length;

  // every route we know the carrier on: from its shipments and from its rates
  const routes = new Map<string, RouteRow>();
  const row = (route: string) =>
    routes.get(route) ?? routes.set(route, { route, shipments: 0, rolledOver: 0, delayed: 0, rates: 0 }).get(route)!;
  ships.forEach((s) => {
    const r = row(`${s.from.city} → ${s.to.city}`);
    r.shipments += 1;
    r.rolledOver += s.rollovers > 0 ? 1 : 0;
    r.delayed += s.stage === 'Delayed' ? 1 : 0;
  });
  rates.forEach((r) => (row(r.route).rates += 1));

  return {
    rates,
    ships,
    total,
    arrived,
    delayed,
    rolledOver,
    other: total - arrived - delayed,
    active: ships.filter((s) => s.stage !== 'Arrived' && s.stage !== 'Completed' && !CLOSED.includes(s.stage)).length,
    reliability: total ? pct(onSchedule, total) : null,
    rolloverRate: total ? pct(rolledOver, total) : null,
    risk: rolloverRisk(rolledOver, total),
    routes: [...routes.values()].sort((a, b) => b.shipments - a.shipments || a.route.localeCompare(b.route)),
    rateCompetitiveness: rateCompetitiveness(rates, allRates),
    lastActivity: [...rates].sort((x, y) => y.date.localeCompare(x.date))[0]?.date ?? '',
    // needs attention: a high rollover risk, or a quarter or more of its shipments delayed
    needsAttention:
      rolloverRisk(rolledOver, total) === 'High' ||
      (total > 0 && pct(delayed, total) >= getSettings().kpis.delayedShare),
  };
}
export type CarrierStats = ReturnType<typeof statsFrom>;

/** For the carrier pages: counted from the real quotes and shipments in the vault */
export function useCarrierStats() {
  const rates = useCarrierRates();
  const { items } = useShipments();
  return (c: Carrier) => statsFrom(c, rates, items);
}
